import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useEffect } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { CoachGroupSwitcher } from "../components/CoachGroupSwitcher"
import { Button } from "@/components/ui/button"
import { FileUploadField } from "@/components/ui/file-upload-field"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Coach-side New form. Coach courses are pinned server-side to:
//   - course_category = "Coach Content"
//   - visibility = "all_members"
//   - public_status = :private (default for coach-authored rows)
// So the form only collects the fields the coach actually controls: title,
// description, status, cover image. Submitting the public-review request is
// a separate action on the show page.
export default function CourseNew({
  currentUser,
  coachGroup,
  coachGroups = [],
}) {
  const form = useForm({
    title: "",
    description: "",
    status: "draft",
    cover_image: null,
  })
  const { transform } = form

  // Harmless on create (there is nothing to detach yet) but the same shape as
  // the edit forms — leaving it out is how the next copy-paste reintroduces
  // the task #524 purge.
  useEffect(() => {
    transform((data) => {
      if (data.cover_image instanceof File) return data
      const { cover_image, ...rest } = data
      return rest
    })
  }, [transform])

  const handleSubmit = (e) => {
    e.preventDefault()
    form.post("/app/coach/courses", {
      forceFormData: true,
    })
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="New Course - Kingdom Factor" />

      <div className="max-w-2xl mx-auto space-y-6">
        <Link
          href="/app/coach/courses"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Courses
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl">New Course</CardTitle>
            <p className="text-sm text-zinc-500">
              Author a course your members in{" "}
              {coachGroups?.length >= 1 ? (
                <CoachGroupSwitcher coachGroup={coachGroup} coachGroups={coachGroups} />
              ) : (
                <span className="font-medium text-zinc-700">{coachGroup?.name || "your group"}</span>
              )}{" "}
              can take. Courses start as drafts and remain private to you until you
              submit for public review.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="course-title">
                  Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="course-title"
                  placeholder="Course title"
                  value={form.data.title}
                  onChange={(e) => form.setData("title", e.target.value)}
                  required
                />
                {form.errors.title && (
                  <p className="text-sm text-red-600">{form.errors.title}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="course-description">Description</Label>
                <Textarea
                  id="course-description"
                  placeholder="Optional description..."
                  className="min-h-[100px]"
                  value={form.data.description}
                  onChange={(e) => form.setData("description", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.data.status}
                  onValueChange={(v) => form.setData("status", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">
                  Published courses are assignable to your groups. Add at least
                  one published lesson before publishing.
                </p>
                {/* The model rejects publishing a course with no published
                    lesson and attaches the error to :status — surface it. */}
                {form.errors.status && (
                  <p className="text-sm text-red-600">{form.errors.status}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="course-cover-image">Cover Image</Label>
                <FileUploadField
                  id="course-cover-image"
                  accept="image/webp,image/png,image/jpeg"
                  value={form.data.cover_image}
                  onChange={(f) => form.setData("cover_image", f)}
                  helpText="WebP, PNG, or JPEG. Max 5 MB."
                />
                {form.errors.cover_image && (
                  <p className="text-sm text-red-600">
                    {form.errors.cover_image}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 pt-4">
                <Button asChild variant="outline" type="button">
                  <Link href="/app/coach/courses">Cancel</Link>
                </Button>
                <Button
                  type="submit"
                  variant="gradient"
                  disabled={form.processing}
                >
                  {form.processing ? "Creating..." : "Create Course"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </CoachLayout>
  )
}
